/**
 * Payment Type Helper
 * Utility functions for handling payment type conversions and display labels
 */

/**
 * Standard payment instrument type to display label mapping
 * @type {Object.<string, string>}
 */
const PAYMENT_TYPE_LABELS = {
    card_instrument: 'Card',
    netbanking_instrument: 'Netbanking',
    upi_instrument: 'UPI',
    qr_instrument: 'UPI QR',
    qris_instrument: 'QRIS',
    wallet_india_instrument: 'Wallet',
    upi_wallet_instrument: 'Wallet',
    virtual_account_dynamic_instrument: 'Virtual Account',
    // Add more mappings as needed
}

/**
 * KCP payment type to display label mapping
 * @type {Object.<string, string>}
 */
const KCP_TYPE_LABELS = {
    card: 'Credit Card & E-Wallet',
    bank: 'Bank Transfer',
}

/**
 * PSP-specific payment type overrides
 * @type {Object.<string, Object.<string, string>>}
 */
const PSP_TYPE_OVERRIDES = {
    doku: {
        qr_instrument: 'qris_instrument',
    },
    // Add more PSP-specific overrides as needed
}

/**
 * Applies PSP-specific overrides to payment type ID
 *
 * @param {string} typeId - The original payment type ID
 * @param {Object} pspResponse - The PSP response object
 * @returns {string} - The potentially modified payment type ID
 */
function applyPspOverrides(typeId, pspResponse) {
    if (!pspResponse || !pspResponse.psp_name) {
        return typeId
    }

    const pspName = pspResponse.psp_name

    const overrides = PSP_TYPE_OVERRIDES[String(pspName)]

    if (overrides && typeId in overrides) {
        return overrides[String(typeId)]
    }

    return typeId
}

/**
 * Converts a payment type ID to a human-readable label
 *
 * @param {string} typeId - The payment type ID to convert
 * @param {Object} [pspResponse={}] - Optional PSP response object for context-specific labels
 * @returns {string} - The human-readable label for the payment type
 */
export function paymentTypeIdToLabel(typeId, pspResponse = {}) {
    if (!typeId) {
        return 'Unknown'
    }

    // Apply any PSP-specific overrides to the type ID
    const effectiveTypeId = applyPspOverrides(typeId, pspResponse)

    // Check standard payment types
    if (effectiveTypeId in PAYMENT_TYPE_LABELS) {
        return PAYMENT_TYPE_LABELS[String(effectiveTypeId)]
    }

    // Handle KCP instrument special case
    if (typeId === 'kcp_instrument' && pspResponse && pspResponse.type in KCP_TYPE_LABELS) {
        return KCP_TYPE_LABELS[String(pspResponse.type)]
    }

    // Handle unknown payment types
    return 'Unknown'
}
